import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import * as React from 'react';
import { Key, memo } from 'react';
import Skeleton from '@mui/material/Skeleton';
import {
  Box,
  Button,
  Pagination,
  PaginationProps,
  Stack,
  SxProps,
} from '@mui/material';
import { useTranslation } from 'next-i18next';
import { Theme } from '@mui/system';
import { getTableRowProps } from '@/components/ui/WrappedTable/helper';
import CatchErrorComp from '@/components/ui/CatchError';
import useTheme from '@/theme/useTheme';
import { useIsomorphicLayoutEffect } from 'ahooks';

export type ColumnProps<T> = {
  [K in keyof T]: InferredColumnProps<T, K>;
}[keyof T];

interface InferredColumnProps<T, K extends keyof T> {
  dataKey?: K;
  title: React.ReactNode;
  width?: number | string;
  numeric?: boolean;

  align?: 'left' | 'right' | 'center';
  render?: (index: number, value: T[K], row: T) => React.ReactNode;
  hidden?: boolean;
}

interface WrappedTableProps<T> {
  columns: ColumnProps<T>[];
  data: (T & {
    animateItem?: boolean;
  })[];
  getRowKey?: (row: T) => Key;
  loading?: boolean;
  loadingRows?: number;
  sxs?: {
    tableContainerSX?: SxProps<Theme>;
    containerSX?: SxProps<Theme>;
  };
  paginationConfig?: PaginationProps;
  anchoredButtonsConfig?: {
    onClickPrev?: () => void;
    onClickNext?: () => void;
  };
}

const WrappedTableCell = (props: {
  column: ColumnProps<any>;
  index: number;
  cellProps?: any;
  d: any;
}) => {
  const { column, index, d, cellProps = {} } = props;
  return (
    <TableCell {...cellProps}>
      {column.render
        ? column.render(index, d[column?.dataKey as unknown as string], d)
        : (d[column?.dataKey as unknown as string] as React.ReactNode)}
    </TableCell>
  );
};

const WrappedTable = <T,>({
  data = [],
  columns = [],
  getRowKey,
  loading,
  loadingRows = 10,
  sxs = {},
  paginationConfig,
  anchoredButtonsConfig,
}: WrappedTableProps<T>) => {
  const [t] = useTranslation();
  const { theme } = useTheme();

  const getCommonCellProps = (column: ColumnProps<T>) => {
    return {
      align: column.align
        ? column.align
        : column.numeric || false
          ? 'right'
          : 'left',
      sx: {
        width: column.width,
      },
    };
  };

  const hasData = !!data.length;

  const showPagination = paginationConfig && hasData;

  const showPrevAndNext =
    anchoredButtonsConfig &&
    hasData &&
    (anchoredButtonsConfig.onClickNext || anchoredButtonsConfig.onClickPrev);

  const containerRef = React.useRef<HTMLDivElement>(null);

  // const [scrollState, setScrollState] = React.useState<
  //   undefined | 'middle' | 'head' | 'rear'
  // >();

  useIsomorphicLayoutEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const isScrollable = container?.scrollWidth > container?.clientWidth;
    // @ts-ignore
    let listener;
    const shadeLeft = container.querySelector('.shade-left') as HTMLDivElement;
    const shadeRight = container.querySelector(
      '.shade-right',
    ) as HTMLDivElement;
    if (isScrollable) {
      shadeLeft.style.display = 'block';
      shadeRight.style.display = 'block';
      listener = (e: MouseEvent) => {
        const target = e.target as HTMLDivElement;
        const { scrollLeft, scrollWidth, clientWidth } = target;
        /**
         * head
         */
        if (scrollLeft === 0) {
          shadeLeft.style.backgroundPositionX = '100%';
          shadeRight.style.backgroundPositionX = '100%';
          /**
           * rear
           */
        } else if (Math.abs(scrollLeft + clientWidth - scrollWidth) <= 1) {
          shadeLeft.style.backgroundPositionX = '0%';
          shadeRight.style.backgroundPositionX = '0%';
          /**
           * middle
           */
        } else {
          shadeLeft.style.backgroundPositionX = '0%';
          shadeRight.style.backgroundPositionX = '100%';
        }
      };
      // @ts-ignore
      container.addEventListener('scroll', listener);
      shadeRight.style.backgroundPositionX = '100%';
      shadeLeft.style.backgroundPositionX = '100%';
    }
    return () => {
      // @ts-ignore
      if (listener && container) {
        container.removeEventListener('scroll', listener);
      }
    };
  }, [data]);

  // console.log(scrollState);

  return (
    <Stack
      gap={2}
      sx={{
        ...(sxs.containerSX || {}),
        position: 'relative',
        ['.shade-left, .shade-right']: {
          position: 'absolute',
          top: 0,
          bottom: 0,
          width: 18,
          zIndex: 10,
          display: 'none',

          height: `calc(100% - ${
            showPagination || showPrevAndNext ? 70 : 0
          }px)`,
          transition: 'background-position 0.3s ease-in-out',
          backgroundSize: '200%',
        },
        ['.shade-right']: {
          right: '1px',
          left: 'auto',
          backgroundImage:
            'linear-gradient(to right, transparent 0%, transparent 50%, #121212 100%)',
          backgroundPositionX: '100%',
          borderTopRightRadius: 'inherit',
          borderBottomRightRadius: 'inherit',
        },
        ['.shade-left']: {
          backgroundImage:
            'linear-gradient(to left, transparent 0%, transparent 50%, #121212 100%)',
          backgroundPositionX: '100%',
          left: '1px',
          borderTopLeftRadius: 'inherit',
          borderBottomLeftRadius: 'inherit',
        },
      }}
      paddingBottom={showPrevAndNext || showPagination ? 2 : undefined}
    >
      <TableContainer
        component={(props) => (
          <Paper
            ref={containerRef}
            {...props}
            sx={{
              boxShadow: 'none',
              border: theme.palette.border.normal,

              ...(sxs.tableContainerSX || {}),
            }}
          />
        )}
      >
        <Box className="shade-left"></Box>
        <Table aria-label="simple table" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => {
                return (
                  <TableCell
                    component="th"
                    key={index}
                    {...getCommonCellProps(column)}
                    sx={{
                      textTransform: 'none !important',
                    }}
                  >
                    {column.title}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !hasData ? (
              Array.from(Array(loadingRows).keys()).map((index) => (
                <TableRow key={index}>
                  {columns.map((column, index) => {
                    return (
                      <TableCell
                        scope="row"
                        key={index}
                        {...getCommonCellProps(column)}
                      >
                        <Skeleton variant="text" />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : hasData ? (
              data.map((d) => {
                return (
                  <TableRow
                    key={getRowKey ? getRowKey(d) : JSON.stringify(d)}
                    {...getTableRowProps(d, loading)}
                  >
                    {columns.map((column, index) => {
                      const cellProps = {
                        scope: 'row',
                        key: index,
                        ...getCommonCellProps(column),
                      };
                      return (
                        <CatchErrorComp
                          key={cellProps.key}
                          fallback={<TableCell {...cellProps}>--</TableCell>}
                          component={
                            <WrappedTableCell
                              cellProps={cellProps}
                              d={d}
                              column={column as unknown as any}
                              index={index}
                              key={cellProps.key}
                            ></WrappedTableCell>
                          }
                        ></CatchErrorComp>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Stack alignItems={'center'}>{t('No Record')}</Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Box className="shade-right"></Box>
      </TableContainer>
      {showPagination && <Pagination {...paginationConfig}></Pagination>}
      {showPrevAndNext && (
        <Stack justifyContent={'center'} gap={2} direction={'row'}>
          <Button
            disabled={!anchoredButtonsConfig.onClickPrev || loading}
            onClick={anchoredButtonsConfig.onClickPrev}
          >
            {t('Prev')}
          </Button>
          <Button
            disabled={!anchoredButtonsConfig.onClickNext || loading}
            onClick={anchoredButtonsConfig.onClickNext}
          >
            {t('Next')}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export default memo(WrappedTable) as typeof WrappedTable;
